import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Row, Col, Badge, Spinner } from "react-bootstrap";
import { MyDiv, SpanTag } from "../Common/Components";
import { Dialog, DialogTitle, DialogContent, Button, DialogActions, Tooltip, IconButton } from '@mui/material';
import NoDataFound from '../Common/noDataFound';
import { MdDescription, MdDelete, MdPictureAsPdf } from "react-icons/md";
import axios from 'axios';
import swal from "sweetalert2";
import { GrDocumentTxt, GrDocumentPdf } from "react-icons/gr";
import { TbFileTypeXls } from "react-icons/tb";
import PropTypes from "prop-types";
import { generateSingleDrawing, generateSplitPreview } from '../../utils/splitPreviewGenerator';
import { getGirth } from '../DrawingHelpers/DrawingHelpers';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Bend count for a drawing card. SF/SSF folds add 2 bends each; angles in
// [170,180] are fold-backs counted as 2. Falls back to a stored drw.bends.
const computeBends = (drw) => {
    if (drw?.bends !== undefined && drw?.bends !== null) return drw.bends;
    const angles = drw?.angles || drw?.nearAngles || drw?.farAngles || [];
    let baseBends = 0;
    for (let i = 0; i < angles.length; i++) {
        const a = Math.abs(angles[i]);
        baseBends += (a >= 170 && a <= 180) ? 2 : 1;
    }
    const hasFolds = drw?.startFoldType || drw?.endFoldType;
    let bends = baseBends;
    if (hasFolds) {
        if (drw?.startFoldType === 'SF' || drw?.startFoldType === 'SSF') bends += 2;
        if (drw?.endFoldType   === 'SF' || drw?.endFoldType   === 'SSF') bends += 2;
    } else {
        bends = Math.max(1, baseBends);
    }
    return bends;
};



// Prefill (canned) Production Info messages the user can tick before generating
// the PDF. Keys are sent to the backend, which holds the printed text.
const PREFILL_OPTIONS = [
    { key: 'cladding', label: 'Cladding Flashing' },
    { key: 'perfect', label: 'Perfect (must be 100%)' },
    { key: 'accurate', label: 'Accurate (dimensions 100%)' },
];

// unitDbId (optional): when provided, documents are read/written against the
// sub-unit (/api/order-units/:unitId/documents) instead of the order. Absent
// for every order-level caller, so their behaviour is unchanged.
function OrderDocuments({ selectedImages, orderUpdates, CoreOrder, department, unitDbId = null, flashingUnitId = null, flashingUnitOrderNumber = null }) {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    const API_TOKEN = localStorage.getItem("token");
    const RolePermission = JSON.parse(localStorage.getItem('role'));
    //console.log('CoreOrder', CoreOrder);
    let { id } = useParams();
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [currentSelectedImages, setSelectedImages] = useState(selectedImages);
    const [orderDepartmentImages, setOrderDepartmentImages] = useState('');
    const [btnLoading, setBtnLoading] = useState(false);
    const [pdfloading, setPdflLoading] = useState(false);
    // PDF preview popup state — when set, an iframe-based dialog opens
    // showing the PDF in place (no new tab).
    const [previewUrl, setPreviewUrl] = useState(null);
    // Flashing PDF — generated on demand and stored at its own S3 key (not in
    // the documents list). flashingPdf holds the status object { exists, fileName }.
    const [generatingFlashingPdf, setGeneratingFlashingPdf] = useState(false);
    const [flashingPdf, setFlashingPdf] = useState(null);
    // Production Info entered at PDF-generate time. System Generated messages are
    // automatic (always applied server-side); the dialog just adds optional
    // prefill picks + manual text. Seeded from the order so a reopened dialog
    // reflects the last selection.
    const [prodInfoText, setProdInfoText] = useState(CoreOrder?.order_production_info || '');
    const [prefillKeys, setPrefillKeys] = useState(Array.isArray(CoreOrder?.order_prefill_messages) ? CoreOrder.order_prefill_messages : []);
    // Save-on-select: persist Production Info to the order the moment the user
    // picks it (no PDF regen, no parent refresh), so the choice survives dialog
    // close/reopen and page reload. Text is debounced so we don't POST per
    // keystroke. Only F/GBI render this UI, so this only fires there.
    const prodInfoSaveTimer = useRef(null);
    const persistProductionInfo = useCallback((text, keys) => {
        const orderId = CoreOrder?._id || id;
        if (!orderId) return;
        axios.put(`${API_BASE_URL}order-production-info/${orderId}?dept=${department}`, {
            order_production_info: text,
            order_prefill_messages: keys,
        }, { headers: { "x-access-token": API_TOKEN } }).catch(() => {});
    }, [API_BASE_URL, API_TOKEN, id, CoreOrder?._id, department]);
    // Seed Production Info from a fresh server read (not the stale order prop) so
    // Flashing and GBI — which share these order-level fields — always show the
    // latest saved selection on open, even after switching between the two dialogs.
    const loadProductionInfo = useCallback(async () => {
        if (department !== 'F' && department !== 'GBI') return;
        const orderId = CoreOrder?._id || id;
        if (!orderId) return;
        try {
            const res = await axios.get(`${API_BASE_URL}order-production-info/${orderId}?dept=${department}`, { headers: { "x-access-token": API_TOKEN } });
            if (res.data?.success) {
                setProdInfoText(res.data.order_production_info || '');
                setPrefillKeys(Array.isArray(res.data.order_prefill_messages) ? res.data.order_prefill_messages : []);
            }
        } catch (e) { /* leave current state on failure */ }
    }, [API_BASE_URL, API_TOKEN, id, CoreOrder?._id, department]);
    const togglePrefill = (key) => {
        setPrefillKeys((prev) => {
            const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
            persistProductionInfo(prodInfoText, next); // immediate on tick
            return next;
        });
    };
    const handleProdInfoTextChange = (value) => {
        const v = value.slice(0, 500);
        setProdInfoText(v);
        if (prodInfoSaveTimer.current) clearTimeout(prodInfoSaveTimer.current);
        prodInfoSaveTimer.current = setTimeout(() => persistProductionInfo(v, prefillKeys), 600);
    };
    // Flush any pending debounced text save on unmount so a quick close doesn't drop it.
    useEffect(() => () => { if (prodInfoSaveTimer.current) clearTimeout(prodInfoSaveTimer.current); }, []);
    // Module-level policy ("Production Info" module): which document methods the
    // dialog offers — generate the system PDF and/or upload a file manually.
    // Defaults to both allowed so a missing module record changes nothing.
    // Two role-permission modules gate the F/GBI document methods, like every
    // other module: "System Generated Girth" (alias SystemGeneratedGirth) and
    // "Manual Upload Girth" (alias ManualUploadGirth). A module that isn't set up —
    // absent from the role map because it wasn't created or is inactive — leaves
    // its method available; restriction only applies once the module exists and
    // the role lacks view permission.
    const moduleAllows = (alias) => !RolePermission?.[alias] || RolePermission[alias].view === "1";
    const allowSystemGenerated = moduleAllows('SystemGeneratedGirth');
    const allowManualUpload = moduleAllows('ManualUploadGirth');
    // A manual "Generate PDF" calls orderUpdates(), which makes the parent
    // refetch the order and briefly unmount this component (a loading spinner),
    // closing the open dialog. We drop a one-shot marker right before that
    // refresh and reopen on remount so the user keeps looking at the PDF they
    // just generated. Keyed by order + department so each dialog is independent.
    const reopenDocDialogKey = (CoreOrder?._id && department)
        ? `docDialogReopen:${CoreOrder._id}:${department}`
        : null;
    // The remount above re-seeds Production Info from CoreOrder, but the refetch
    // that updated CoreOrder may not have returned yet — so the just-picked
    // prefills/manual text would re-init blank. Stash the user's selection on a
    // manual generate and restore it on remount so their picks always survive.
    const prodInfoStashKey = (CoreOrder?._id && department)
        ? `prodInfoStash:${CoreOrder._id}:${department}`
        : null;
    const handleOpenDialog = () => {
        loadOrderDepartmentImages();
        // Seed Production Info from the server (fresh), not the stale order prop —
        // this is what keeps Flashing/GBI in sync and stops the selection clearing
        // after switching between the two dialogs.
        loadProductionInfo();
        setOpenDialog(true);
        setSelectedFiles([]);
    };

    const handleCloseDialog = () => {
        // User explicitly closed — drop any pending reopen + stashed selection so
        // a late refresh can't pop it back up or restore stale picks.
        if (reopenDocDialogKey) sessionStorage.removeItem(reopenDocDialogKey);
        if (prodInfoStashKey) sessionStorage.removeItem(prodInfoStashKey);
        setOpenDialog(false);
        setOrderDepartmentImages();
    };

    const loadOrderDepartmentImages = useCallback(async () => {
    const url = unitDbId
      ? `${API_BASE_URL}api/order-units/${unitDbId}/documents`
      : `${API_BASE_URL}fetch-specific-order-design-images/${id}/${department}`;
    try {
      const res = await axios.get(url, {
        headers: {
          "x-access-token": localStorage.getItem("token"),
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });
      setOrderDepartmentImages(res.data);
      // Return so callers (e.g. downloadDrawingsPDF) can immediately pick
      // the latest auto-PDF to feed into the popup preview, without waiting
      // on the next render.
      return res.data;
    } catch (error) {
      console.log(error);
      return null;
    }
  }, [id, department, unitDbId]);

    useEffect(() => {
        loadOrderDepartmentImages();
    }, [loadOrderDepartmentImages]);

    // Re-seed Production Info when the order finishes loading / changes.
    useEffect(() => {
        setProdInfoText(CoreOrder?.order_production_info || '');
        setPrefillKeys(Array.isArray(CoreOrder?.order_prefill_messages) ? CoreOrder.order_prefill_messages : []);
    }, [CoreOrder?._id]);

    // ---------- Flashing PDF ----------
    const flashingHeaders = { "x-access-token": API_TOKEN };

    // Checks S3 for a saved Flashing PDF so the tile and button label reflect
    // whether one already exists. Any error is treated as "no PDF".
    const refreshFlashingPdfStatus = useCallback(async () => {
        if (!id || department !== 'F') return;
        // Per-unit when a sub-unit is selected, order-level otherwise.
        const url = flashingUnitId
            ? `${API_BASE_URL}api/flashing-pdf/unit/${flashingUnitId}/status`
            : `${API_BASE_URL}api/flashing-pdf/status/${id}`;
        try {
            const res = await axios.get(url, {
                headers: { "x-access-token": API_TOKEN },
            });
            setFlashingPdf(res.data);
        } catch (error) {
            setFlashingPdf({ exists: false });
        }
    }, [id, department, API_TOKEN, flashingUnitId]);

    useEffect(() => {
        refreshFlashingPdfStatus();
    }, [refreshFlashingPdfStatus]);

    // The PDF cards must visually match the on-screen Drawings list, so the
    // frontend renders each drawing here with the same helper that list uses
    // and POSTs the PNGs; the backend only composes the HTML and rasterises.
    // `silent` (used by the auto-regenerate triggers) suppresses the info/error
    // popups so a background refresh never interrupts the user.
    const handleGenerateFlashingPdf = async ({ silent = false } = {}) => {
        if (!id || !CoreOrder?.order_unique_id) {
            if (!silent) swal.fire({ text: "Order info missing.", icon: "error" });
            return;
        }
        if (generatingFlashingPdf) return; // re-entrancy guard (manual + auto)
        setGeneratingFlashingPdf(true);
        try {
            const drawingsRes = await axios.get(
                `${API_BASE_URL}api/orders/id/${id}/order/drawings`,
                { headers: flashingHeaders }
            );
            let drawings = Array.isArray(drawingsRes.data) ? drawingsRes.data : [];
            // Per-unit PDF: keep only this sub-unit's drawings. The drawings
            // list carries a unit-suffixed orderNumber (e.g. "IN101086.U1").
            if (flashingUnitOrderNumber) {
                drawings = drawings.filter(d => d.orderNumber === flashingUnitOrderNumber);
            }
            if (drawings.length === 0) {
                if (!silent) swal.fire({ text: "No Flashing drawings on this order yet.", icon: "info" });
                return;
            }

            // The on-screen tag is the SWI Shape ID, not materialRow.tag — resolve
            // the mapping up front so each card shows the same tag the user sees.
            const allJobIds = [];
            for (const drw of drawings) {
                if (Array.isArray(drw.swiJobIds)) {
                    for (const jobId of drw.swiJobIds) {
                        if (jobId) allJobIds.push(jobId);
                    }
                }
            }
            let shapeIdMap = {};
            if (allJobIds.length > 0) {
                try {
                    const swiRes = await axios.post(
                        `${API_BASE_URL}api/swi/check-job-status`,
                        { jobIds: allJobIds },
                        { headers: flashingHeaders }
                    );
                    shapeIdMap = swiRes.data?.shapeIdMap || {};
                } catch (_) { /* fall back to row.tag if the SWI lookup fails */ }
            }

            const entries = [];
            for (const drw of drawings) {
                const firstRow = drw.materialRows?.[0] || {};
                const splitInto = Number(firstRow.splitInto || 1);
                const isTaper = drw.isTaper === true;
                const totalQty = (drw.materialRows || []).reduce(
                    (s, r) => s + (Number(r.qty ?? r.quantity) || 0), 0
                );
                const baseLength = Number(firstRow.length) || 1200;
                const bends = computeBends(drw);
                // One {qty, length, tag} entry per material row. Tag is the SWI
                // Shape ID at the same row index, falling back to row.tag.
                const allRows = (drw.materialRows || [])
                    .map((r, idx) => {
                        const swiJobId = drw.swiJobIds?.[idx] || '';
                        const fallbackTag = (r.tag || '').trim();
                        return {
                            qty:    Number(r.qty ?? r.quantity) || 0,
                            length: Number(r.length) || 0,
                            tag:    shapeIdMap[swiJobId] || fallbackTag || '–',
                        };
                    })
                    .filter((r) => r.qty || r.length);
                const baseMeta = {
                    material:  firstRow.material  || drw.material  || 'N/A',
                    color:     firstRow.color     || drw.color     || 'N/A',
                    thickness: drw.thickness      || firstRow.thickness || '0.55',
                    bends,
                };
                const sharedRenderParams = {
                    angles:                  drw.angles || [],
                    direction:               drw.direction || 'Right',
                    firstSegmentAngle:       drw.firstSegmentAngle,
                    reverseColor:            drw.reverseColor || false,
                    flipH:                   drw.flipH || false,
                    flipV:                   drw.flipV || false,
                    startFoldType:           drw.startFoldType,
                    startFoldLength:         drw.startFoldLength || 0,
                    startFoldDirection:      drw.startFoldDirection,
                    endFoldType:             drw.endFoldType,
                    endFoldLength:           drw.endFoldLength || 0,
                    endFoldDirection:        drw.endFoldDirection,
                    startFoldGap:            drw.startFoldGap || 0,
                    endFoldGap:              drw.endFoldGap || 0,
                    // The renderer needs the originals + absolute angles to
                    // position fold labels and orient segments correctly.
                    originalStartFoldLength: drw.startFoldLength || 0,
                    originalEndFoldLength:   drw.endFoldLength || 0,
                    segmentAbsoluteAngles:   drw.segmentAbsoluteAngles || [],
                    labelOffsets:            drw.labelOffsets,
                    // Match the on-screen Drawings view (DrawingDetailsTab renders
                    // at 1000x700, fontSize 24). Saved label offsets are raw pixels
                    // at that scale, so the PDF must render at the same size/font or
                    // the labels collapse together. PDF-only — on-screen untouched.
                    fontSize:                24,
                };

                if (!isTaper) {
                    const result = await generateSingleDrawing({
                        ...sharedRenderParams,
                        farLengths:         drw.farLengths || drw.lengths || [],
                        nearLengths:        [],
                        interpolationRatio: 0,
                        width:  1000,
                        height: 700,
                    });
                    entries.push({
                        ...baseMeta,
                        isTaper:      false,
                        drawingPng:   result?.image || '',
                        girth:        Math.round(getGirth(
                            drw.lengths || [],
                            drw.startFoldType, drw.startFoldLength || 0,
                            drw.endFoldType,   drw.endFoldLength   || 0,
                        )),
                        materialRows: allRows,
                    });
                } else if (splitInto <= 1) {
                    const farLengths  = drw.farLengths  || drw.lengths || [];
                    const nearLengths = drw.nearLengths || [];
                    const [farResult, nearResult] = await Promise.all([
                        generateSingleDrawing({
                            ...sharedRenderParams,
                            farLengths, nearLengths,
                            interpolationRatio: 0,
                            width:  1000, height: 700,
                        }),
                        generateSingleDrawing({
                            ...sharedRenderParams,
                            farLengths, nearLengths,
                            interpolationRatio: 1,
                            width:  1000, height: 700,
                        }),
                    ]);
                    const plainFarGirth = Math.round(getGirth(
                        drw.farLengths || drw.lengths || [],
                        drw.startFoldType, drw.startFoldLength || 0,
                        drw.endFoldType,   drw.endFoldLength   || 0,
                    ));
                    const plainNearGirth = Math.round(getGirth(
                        drw.nearLengths || drw.lengths || [],
                        drw.startFoldType, drw.startFoldLength || 0,
                        drw.endFoldType,   drw.endFoldLength   || 0,
                    ));
                    entries.push({
                        ...baseMeta,
                        isTaper:      true,
                        farPng:       farResult?.image  || '',
                        nearPng:      nearResult?.image || '',
                        farGirth:     plainFarGirth,
                        nearGirth:    plainNearGirth,
                        materialRows: allRows,
                    });
                } else {
                    // The drawings-list endpoint omits split-specific fields, so
                    // fetch the full template before rendering split pieces.
                    let fullTpl = drw;
                    try {
                        const tplRes = await axios.get(
                            `${API_BASE_URL}api/templates/${drw.id || drw.templateId}`,
                            { headers: flashingHeaders }
                        );
                        fullTpl = tplRes.data?.data || tplRes.data || drw;
                    } catch (_) { /* fall back to drawings-list data */ }

                    const farLens  = (fullTpl.farLengths  || fullTpl.lengths || []).map(Number);
                    const nearLens = (fullTpl.nearLengths || fullTpl.lengths || []).map(Number);
                    const splitAngles = fullTpl.farAngles || fullTpl.angles || [];

                    const farGirthFull = getGirth(
                        farLens,
                        fullTpl.startFoldType, fullTpl.startFoldLength || 0,
                        fullTpl.endFoldType,   fullTpl.endFoldLength   || 0,
                    );
                    const nearGirthFull = getGirth(
                        nearLens,
                        fullTpl.startFoldType, fullTpl.startFoldLength || 0,
                        fullTpl.endFoldType,   fullTpl.endFoldLength   || 0,
                    );
                    const girthStep = (farGirthFull - nearGirthFull) / splitInto;

                    const pieceConfigs = [];
                    for (let i = 0; i < splitInto; i++) {
                        const pieceFar = farLens.map((farLen, j) => {
                            const nearLen = nearLens[j] !== undefined ? nearLens[j] : farLen;
                            const step = (nearLen - farLen) / splitInto;
                            return Math.round(farLen + step * i);
                        });
                        const pieceNear = farLens.map((farLen, j) => {
                            const nearLen = nearLens[j] !== undefined ? nearLens[j] : farLen;
                            const step = (nearLen - farLen) / splitInto;
                            return Math.round(farLen + step * (i + 1));
                        });
                        pieceConfigs.push({ index: i, pieceFar, pieceNear });
                    }

                    const renderResults = await Promise.all(pieceConfigs.map(cfg =>
                        generateSplitPreview({
                            farLengths:  cfg.pieceFar,
                            nearLengths: cfg.pieceNear,
                            angles:      splitAngles,
                            direction:          fullTpl.direction || 'Right',
                            firstSegmentAngle:  fullTpl.firstSegmentAngle,
                            reverseColor:       fullTpl.reverseColor || false,
                            flipH:              fullTpl.flipH || false,
                            flipV:              fullTpl.flipV || false,
                            startFoldType:      fullTpl.startFoldType,
                            startFoldLength:    fullTpl.startFoldLength || 0,
                            startFoldDirection: fullTpl.startFoldDirection,
                            endFoldType:        fullTpl.endFoldType,
                            endFoldLength:      fullTpl.endFoldLength || 0,
                            endFoldDirection:   fullTpl.endFoldDirection,
                            startFoldGap:       fullTpl.startFoldGap || 0,
                            endFoldGap:         fullTpl.endFoldGap || 0,
                            originalFarLengths:  farLens,
                            originalNearLengths: nearLens,
                        }).then(result => ({ ...cfg, result }))
                    ));

                    const perPieceQty = totalQty / splitInto;
                    for (const { index, result } of renderResults) {
                        const pieceJobId = drw.swiJobIds?.[index] || drw.swiJobIds?.[0] || '';
                        const tag = shapeIdMap[pieceJobId] || '–';
                        // Pieces are rendered far → near; piece 0 is the far end.
                        const reverseIndex = splitInto - 1 - index;
                        const splitFarGirth  = nearGirthFull + (reverseIndex + 1) * girthStep;
                        const splitNearGirth = nearGirthFull +  reverseIndex      * girthStep;
                        entries.push({
                            ...baseMeta,
                            isTaper:      true,
                            farPng:       result?.farImage  || '',
                            nearPng:      result?.nearImage || '',
                            farGirth:     Math.round(splitFarGirth),
                            nearGirth:    Math.round(splitNearGirth),
                            materialRows: [{ qty: perPieceQty, length: baseLength, tag }],
                            splitIndex:   index + 1,
                            splitTotal:   splitInto,
                        });
                    }
                }
            }

            const generateUrl = flashingUnitId
                ? `${API_BASE_URL}api/flashing-pdf/unit/${flashingUnitId}`
                : `${API_BASE_URL}api/flashing-pdf/${id}`;
            await axios.post(
                generateUrl,
                {
                    drawings: entries,
                    order_production_info: prodInfoText,
                    order_prefill_messages: prefillKeys,
                    order_include_system_info: true,
                },
                { headers: flashingHeaders, responseType: 'blob' }
            );

            await refreshFlashingPdfStatus();
            // The generated PDF is now registered as a document (order_images_f /
            // unit.documents); refresh the parent so the QC gates (Move to QC /
            // Move to Production) re-evaluate without a manual reload.
            await loadOrderDepartmentImages();
            // Manual generate only: the parent refresh below remounts us, so flag
            // a reopen. Silent auto-regen runs in the background and must NOT pop
            // the dialog open.
            if (!silent && reopenDocDialogKey) sessionStorage.setItem(reopenDocDialogKey, '1');
            // Stash the user's Production Info picks so the remount restores them
            // instead of re-seeding blank from a not-yet-refetched CoreOrder.
            if (!silent && prodInfoStashKey) sessionStorage.setItem(prodInfoStashKey, JSON.stringify({ prodInfoText, prefillKeys }));
            // Only a manual generate refreshes the parent (full order refetch) so
            // its QC gates re-evaluate. A silent auto-regen (triggered by a drawing
            // edit) must stay invisible — calling orderUpdates() here visibly
            // reloads the whole order page right after the edit. The local status +
            // dialog refresh above already keep this component current; the QC gate
            // catches up on the next natural interaction.
            if (!silent && orderUpdates) orderUpdates();
        } catch (error) {
            const status = error?.response?.status;
            const message = status === 404
                ? "No Flashing drawings on this order yet."
                : "Failed to generate Flashing PDF.";
            if (!silent) swal.fire({ text: message, icon: status === 404 ? "info" : "error" });
        } finally {
            setGeneratingFlashingPdf(false);
        }
    };

    // ---------- Auto-regenerate after drawing changes ----------
    // Flashing PDFs are rendered in the browser, so we can't regenerate them with
    // a pure backend hook like GBI/AWF. Instead, a drawing save/delete sets a
    // per-order "stale" flag (keyed by the order's Mongo _id, shared with the
    // drawing screens); when this component is mounted on the order page we
    // silently regenerate so the saved sheet keeps up with the drawings.
    //
    // Guards (so it never surprises anyone or breaks the manual flow):
    //   - Flashing department only.
    //   - Creates the sheet even when none exists yet, so adding the first
    //     drawing produces a PDF; handleGenerateFlashingPdf bails silently when
    //     the order has zero Flashing drawings, so it only ever creates one when
    //     there is actually something to draw.
    //   - Skipped while a generation is already running (re-entrancy).
    //   - Fully silent + fire-and-forget; a failure just leaves it stale (the
    //     manual "Generate PDF" button still works exactly as before).
    const flashingStaleKey = CoreOrder?._id ? `flashingPdfStale:${CoreOrder._id}` : null;
    const tryAutoRegenFlashing = useCallback(() => {
        if (department !== 'F' || !flashingStaleKey) return;
        if (localStorage.getItem(flashingStaleKey) !== '1') return;
        if (generatingFlashingPdf) return;
        // Clear first so the status refresh inside generate can't re-trigger us.
        localStorage.removeItem(flashingStaleKey);
        handleGenerateFlashingPdf({ silent: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [department, flashingStaleKey, generatingFlashingPdf]);

    // Save case: editing a drawing navigates away and back, so the order page
    // remounts. Once the PDF status has loaded, consume the stale flag.
    useEffect(() => {
        tryAutoRegenFlashing();
    }, [tryAutoRegenFlashing]);

    // Delete/re-push case: happens in-page (no remount). The drawing screens
    // dispatch this event; regenerate live if it's for this order.
    useEffect(() => {
        const onChanged = (e) => {
            const changedId = e?.detail?.orderId;
            if (changedId && CoreOrder?._id && String(changedId) !== String(CoreOrder._id)) return;
            if (flashingStaleKey) localStorage.setItem(flashingStaleKey, '1');
            tryAutoRegenFlashing();
        };
        window.addEventListener('flashing-drawings-changed', onChanged);
        return () => window.removeEventListener('flashing-drawings-changed', onChanged);
    }, [flashingStaleKey, CoreOrder?._id, tryAutoRegenFlashing]);

    // Backstop: even if a trigger above is missed, opening the documents dialog
    // refreshes a stale PDF, so the user can never view an out-of-date sheet.
    useEffect(() => {
        if (openDialog) tryAutoRegenFlashing();
    }, [openDialog, tryAutoRegenFlashing]);

    // Reopen the dialog after a manual-generate remount (see reopenDocDialogKey).
    // One-shot: consume the marker on mount so it never lingers. Also restore the
    // Production Info the user had selected, which the fresh mount would otherwise
    // re-seed blank from a CoreOrder that hasn't finished refetching.
    useEffect(() => {
        if (reopenDocDialogKey && sessionStorage.getItem(reopenDocDialogKey) === '1') {
            sessionStorage.removeItem(reopenDocDialogKey);
            setOpenDialog(true);
            loadOrderDepartmentImages();
            if (prodInfoStashKey) {
                try {
                    const saved = JSON.parse(sessionStorage.getItem(prodInfoStashKey) || 'null');
                    if (saved) {
                        if (typeof saved.prodInfoText === 'string') setProdInfoText(saved.prodInfoText);
                        if (Array.isArray(saved.prefillKeys)) setPrefillKeys(saved.prefillKeys);
                    }
                } catch (e) { /* malformed stash — ignore, fall back to seeded values */ }
            }
        }
        if (prodInfoStashKey) sessionStorage.removeItem(prodInfoStashKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleDeleteFlashingPdf = async () => {
        if (!id) return;
        const url = flashingUnitId
            ? `${API_BASE_URL}api/flashing-pdf/unit/${flashingUnitId}`
            : `${API_BASE_URL}api/flashing-pdf/${id}`;
        try {
            await axios.delete(url, { headers: flashingHeaders });
            await refreshFlashingPdfStatus();
            // Deletion also unregisters the document; refresh so the QC gates
            // re-evaluate (the button may hide again if no document remains).
            await loadOrderDepartmentImages();
            if (orderUpdates) orderUpdates();
            swal.fire({ text: "Flashing PDF deleted", icon: "success" });
        } catch (error) {
            swal.fire({
                text: error.response?.data?.message || "Failed to delete Flashing PDF",
                icon: "error",
            });
        }
    };

    // Streams the saved PDF as a blob and shows it in the existing preview popup.
    const handleOpenFlashingPreview = async () => {
        // Block preview mid-regen: the saved S3 file is still the old PDF until the
        // regenerate finishes uploading, so opening now would show a stale sheet.
        if (generatingFlashingPdf) return;
        if (!id || !flashingPdf?.exists) return;
        const url = flashingUnitId
            ? `${API_BASE_URL}api/flashing-pdf/unit/${flashingUnitId}/file`
            : `${API_BASE_URL}api/flashing-pdf/file/${id}`;
        try {
            const res = await axios.get(url, {
                headers: flashingHeaders,
                responseType: 'blob',
            });
            const blob = new Blob([res.data], { type: 'application/pdf' });
            setPreviewUrl(window.URL.createObjectURL(blob));
        } catch (error) {
            swal.fire({
                text: error.response?.data?.message || "Failed to load Flashing PDF",
                icon: "error",
            });
        }
    };

    const handleDeleteImage = async index => {
        const url = unitDbId
            ? `${API_BASE_URL}api/order-units/${unitDbId}/documents/${index}`
            : `${API_BASE_URL}delete-specific-order-design-images/${id}/${department}/${index}`;
        try {
            await axios.delete(url, {
                headers: {
                    "x-access-token": localStorage.getItem("token"),
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
            });
            // Keep the dialog open + no toast — user typically deletes the
            // auto-PDF to regenerate immediately. Refresh the grid so the
            // card disappears and the button flips back to "Generate PDF".
            await loadOrderDepartmentImages();
            orderUpdates();
        } catch (error) {
            swal.fire({
                text: error.response?.data || 'Failed to delete document',
                icon: 'error',
            });
        }
    };

    const handleFileSelect = (e) => {
        setSelectedFiles(Array.from(e.target.files));
    };

    const handleSubmit = e => {
        e.preventDefault();
        setBtnLoading(true);
        const formData = new FormData();
        selectedFiles.forEach(file => {
        formData.append("documents", file);
        });

        const headers = {
          "x-access-token": localStorage.getItem("token"),
          Accept: "application/json",
          "Content-Type": "multipart/form-data",
        };
        // Sub-unit upload posts to the per-unit endpoint; order-level upload
        // keeps the original PUT to update-specific-order-design-images.
        const request = unitDbId
          ? axios.post(`${API_BASE_URL}api/order-units/${unitDbId}/documents`, formData, { headers })
          : axios.put(`${API_BASE_URL}update-specific-order-design-images/${id}/${department}`, formData, { headers });

        request
        .then(res => {
            if (res.status === 200) {
            setOpenDialog(false);
            swal.fire({
                text: "Successfully Saved",
                icon: "success",
            });
            orderUpdates();
            }
        })
        .catch(error => {
            swal.fire({
            text: error.response?.data || "An error occurred",
            icon: "error",
            });
        })
        .finally(() => {
            setBtnLoading(false);
        });
    };

    const getFileType = (url) => {
        const urlWithoutQuery = url.split('?')[0];
        const extension = urlWithoutQuery.split('.').pop().toLowerCase();
        switch (extension) {
            case 'png':
            case 'jpeg':
            case 'jpg':
            case 'gif':
                return 'image';
            case 'pdf':
                return 'pdf';
            case 'doc':
            case 'docx':
                return 'doc';
            case 'xlsx':
                return 'xlsx';
            default:
                return 'unknown';
        }
    };

    // Returns the filename string rendered under each document card. Two cases:
    //   1. Auto-generated PDFs are stored at S3 key
    //      `barcodes/<orderNo>/<dept>_barcode.pdf`. The displayed name
    //      flattens that into "<orderNo>_<dept>_barcode.pdf" so users see
    //      something like "IN101079_GBI_barcode.pdf".
    //   2. Manual uploads go through multer-s3 and are stored at
    //      `<Month>-<Year>/<uuid>-<original-filename>.<ext>`. The leading
    //      UUID prefix is stripped so the user sees just the original filename
    //      they uploaded, not the generated hash.
    // Falls back to "document" when the input has no key/url at all.
    const getDisplayFilename = (depImage) => {
        const keyOrUrl = depImage?.key || depImage?.url || '';
        if (!keyOrUrl) return 'document';
        const cleanPath = keyOrUrl.split('?')[0];
        const parts = cleanPath.split('/').filter(Boolean);
        const isAutoBarcode = depImage?.key && parts[0] === 'barcodes' && parts.length >= 3;
        if (isAutoBarcode) {
            return `${parts[1]}_${parts[parts.length - 1]}`;
        }
        const basename = parts[parts.length - 1] || 'document';
        // Strip leading multer-s3 UUID prefix (8-4-4-4-12 hex chars + dash)
        // so the displayed name is the meaningful original filename.
        return basename.replace(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-/i, '');
    };

    const departmentLabels = {
        'F': 'Flashing',
        'FG': 'Fascia Gutter',
        'J': 'Jobbing',
        'CL': 'Cladding',
        'GBI': 'GBI',
        'ROOF': 'Roofing',
    };

    // The generated Flashing PDF lives at key `flashing-pdf/<no>.pdf` and is now
    // registered in order_images_f / unit.documents (so the QC gates see it). It
    // already renders via the dedicated Flashing tile below, so it must be hidden
    // here to avoid showing the same PDF twice.
    const isFlashingGeneratedKey = (depImage) =>
        (depImage?.key || depImage?.url || '').split('?')[0].includes('flashing-pdf/');

    const renderImagesByDepartment = (depImages) => {
        // Map over the ORIGINAL array (don't filter) so `index` stays aligned
        // with the backend documents array that handleDeleteImage deletes by.
        if (depImages.length) {
            return depImages.map((depImage, index) => {
                if (isFlashingGeneratedKey(depImage)) return null;
                const fileType = getFileType(depImage.url);
                const filename = getDisplayFilename(depImage);
                return (
                    <Col key={index} md={3} className="mb-3">
                        <MyDiv>
                            <SpanTag className="text-center">
                                {fileType === 'image' && (
                                    <img src={depImage.url} alt={`Order Image ${index + 1}`} />
                                )}
                                {fileType === 'pdf' && (
                                    <GrDocumentPdf
                                        className='DocIcon'
                                        style={{ cursor: 'pointer' }}
                                        title="Preview PDF"
                                        onClick={() => setPreviewUrl(depImage.url)}
                                    />
                                )}
                                {fileType === 'doc' && (
                                    <GrDocumentTxt className='DocIcon' />
                                )}
                                {fileType === 'xlsx' && (
                                    <TbFileTypeXls className='DocIcon' />
                                )}
                                {fileType === 'unknown' && (
                                    <span>Unknown file type for Order {index + 1}</span>
                                )}
                            </SpanTag>

                            <div
                                className="docFilename"
                                style={{
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    textAlign: 'center',
                                    wordBreak: 'break-all',
                                    margin: '4px 0',
                                }}
                                title={filename}
                            >
                                {filename}
                            </div>

                            <Tooltip title="Delete Image">
                                <IconButton aria-label="delete" color="secondary" onClick={() => handleDeleteImage(index)}><MdDelete /></IconButton>
                            </Tooltip>

                            {fileType === 'pdf' ? (
                                <a
                                    href="#"
                                    onClick={(e) => { e.preventDefault(); setPreviewUrl(depImage.url); }}
                                    title='Preview'
                                    style={{ cursor: 'pointer' }}
                                >
                                    Preview
                                </a>
                            ) : (
                                <a href={depImage.url} download target="_blank" rel="noopener noreferrer" title='Download'>Download</a>
                            )}
                        </MyDiv>
                    </Col>
                );
            });
        }
        return null;
    };

const generateGbiSystemPdf = async () => {
    setPdflLoading(true);
    try {
        // Backend's regenerateAutoPdf replaces any prior auto-PDF on the
        // order, uploads to S3 (stable key barcodes/<orderNo>/<deptCode>_barcode.pdf),
        // and pushes the key into order_images_gbi — same array this dialog reads from.
        const response = await axios.post(
            `${API_BASE_URL}api/gbi-auto-pdf/${id}`,
            {
                order_production_info: prodInfoText,
                order_prefill_messages: prefillKeys,
                order_include_system_info: true,
            },
            { headers: { 'x-access-token': API_TOKEN } }
        );
        if (response.data?.reason === 'no-entries') {
            swal.fire({ text: 'No GBI line items on this order yet.', icon: 'info' });
        } else if (response.data?.success) {
            // Just attach; no toast, no auto-popup. User clicks Preview in
            // the grid to view when they want.
            await loadOrderDepartmentImages();
            // The parent refresh remounts us; flag a reopen so the dialog the
            // user generated from stays open, and stash the Production Info picks
            // so the remount restores them instead of re-seeding blank.
            if (reopenDocDialogKey) sessionStorage.setItem(reopenDocDialogKey, '1');
            if (prodInfoStashKey) sessionStorage.setItem(prodInfoStashKey, JSON.stringify({ prodInfoText, prefillKeys }));
            orderUpdates();
        } else {
            swal.fire({ text: 'Could not attach GBI PDF', icon: 'error' });
        }
    } catch (err) {
        swal.fire({ text: err.response?.data?.message || err.message || 'Failed to generate GBI PDF', icon: 'error' });
    } finally {
        setPdflLoading(false);
    }
};

const downloadDrawingsPDF = async () => {
    setPdflLoading(true);
    try {
        console.log("id", id);
        
        // Delete existing system-generated PDFs before creating new one
        if (orderDepartmentImages && orderDepartmentImages?.imageUrls.length > 0) {
            // Create array to store indices to delete (in reverse order to avoid index shifting issues)
            const indicesToDelete = [];
            
            orderDepartmentImages.imageUrls.forEach((imageObj, index) => {
                const imageUrl = imageObj.url;
                // Extract filename from the full path (after the UUID)
                // Pattern: October-2025/uuid-Drawings_orderId_timestamp.pdf
                const urlParts = imageUrl.split('/');
                const filename = urlParts[urlParts.length - 1];
                
                // Check if filename contains 'Drawings_' after the UUID prefix
                // This identifies system-generated PDFs
                if (filename.includes('-Drawings_')) {
                    indicesToDelete.push(index);
                }
            });
            
            // Delete in reverse order to maintain correct indices
            for (let i = indicesToDelete.length - 1; i >= 0; i--) {
                const index = indicesToDelete[i];
                try {
                    await axios.delete(
                        `${API_BASE_URL}delete-specific-order-design-images/${id}/${department}/${index}`,
                        {
                            headers: {
                                'x-access-token': API_TOKEN,
                                'Accept': 'application/json',
                                'Content-Type': 'application/json',
                            },
                        }
                    );
                    console.log(`Deleted existing system-generated PDF at index ${index}`);
                } catch (deleteError) {
                    console.warn(`Failed to delete PDF at index ${index}:`, deleteError);
                    // Continue with generation even if delete fails
                }
            }
            
            // Reload images after deletion if any were deleted
            if (indicesToDelete.length > 0) {
                await loadOrderDepartmentImages();
            }
        }
        
        const apiUrl = `${API_BASE_URL}api/templates/get-system-generated-drawing/${id}`;
        
        // Request the PDF as a blob
        const response = await fetch(apiUrl, {
            method: 'GET',
            headers: {
                'x-access-token': API_TOKEN,
                'Accept': 'application/pdf',
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText || 'Failed to generate drawings PDF');
        }

        console.log('PDF generation response:', response);
        console.log('Content-Type:', response.headers.get('content-type'));

        const pdfBlob = await response.blob();
        console.log("blob", pdfBlob);
        console.log("blob type:", pdfBlob.type);
        console.log("blob size:", pdfBlob.size);

        // Validate that we got a PDF
        if (pdfBlob.size < 1000) {
            const text = await pdfBlob.text();
            console.error('Received small response:', text);
            throw new Error('Invalid PDF received from server');
        }

        // The PDF is uploaded with a filename of the shape
        // "<order_unique_id>_F_barcode.pdf" (F = Flashing department code),
        // which is the same shape the GBI auto-PDF flow uses. This makes the
        // file recognisable as a system-generated document in the
        // `hasGeneratedPdf` check below and in the document card's displayed
        // filename. If order_unique_id isn't on CoreOrder yet (very early in
        // an order's lifecycle), fall back to the legacy timestamped name so
        // the upload still succeeds.
        const baseName = CoreOrder?.order_unique_id
            ? `${CoreOrder.order_unique_id}_F_barcode.pdf`
            : `Drawings_${id}_${Date.now()}.pdf`;
        const formData = new FormData();
        formData.append('documents', pdfBlob, baseName);

        // Upload the PDF file
        const uploadUrl = `${API_BASE_URL}update-specific-order-design-images/${id}/${department}`;
        await axios.put(uploadUrl, formData, {
            headers: {
                'x-access-token': API_TOKEN,
                'Accept': 'application/json',
                'Content-Type': 'multipart/form-data',
            },
        });

        // Just attach; no toast, no auto-popup. User clicks Preview in the
        // grid to view when they want.
        setOpenDialog(true);
        await loadOrderDepartmentImages();
        orderUpdates();

    } catch (err) {
        console.error('Error generating or uploading drawings PDF:', err);
        swal.fire({
            text: err.message || 'Failed to generate or upload PDF',
            icon: 'error',
        });
    } finally {
        setPdflLoading(false);
    }
};

    // True if the current documents list contains a system-generated PDF
    // for this dept (GBI: `_barcode.pdf` key; Flashing: legacy `_Drawings_`
    // or new `_F_barcode.pdf`). Drives the button label: "Generate PDF"
    // when none yet, "Regenerate PDF" once one exists.
    const hasGeneratedPdf = (() => {
        const list = orderDepartmentImages?.imageUrls;
        if (!Array.isArray(list)) return false;
        return list.some(item => {
            const k = (item?.key || item?.url || '').split('?')[0];
            return /_barcode\.pdf$/i.test(k) || /-Drawings_/i.test(k);
        });
    })();

    return (
        <React.Fragment>
            <Tooltip title="Documents" className='ms-2 me-2'>
                <IconButton aria-label="fingerprint" sx={{ color: "#fff", width: "40px", height: "40px", backgroundColor: "#229b2c ", '&:hover': { backgroundColor: "#135c19" } }} onClick={(e) => handleOpenDialog()} >
                    <MdDescription />
                </IconButton>
            </Tooltip>
            <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="md" className="GeneralModal">
                <DialogTitle className='space-between'> {departmentLabels[department] ? `${departmentLabels[department]} -` : ''} Item Documents  <Button onClick={handleCloseDialog} className="btn"> X </Button></DialogTitle>
                <DialogContent>
                    <MyDiv className="documentContainer">
                        <Row className='justify-content-end'>
                            {orderDepartmentImages && orderDepartmentImages?.imageUrls.length > 0 ? (
                                <>
                                    {renderImagesByDepartment(orderDepartmentImages.imageUrls)}
                                </>
                            ) : (
                                !(department === 'F' && flashingPdf?.exists) && <NoDataFound />
                            )}
                            {/* Saved Flashing PDF tile — the PDF lives at its own
                                S3 key, not in the documents list, so it renders as
                                a separate card. Click to preview, trash to delete. */}
                            {department === 'F' && flashingPdf?.exists && (
                                <Col md={3} className="mb-3">
                                    <MyDiv>
                                        <SpanTag
                                            className="text-center"
                                            style={{ cursor: generatingFlashingPdf ? 'wait' : 'pointer', opacity: generatingFlashingPdf ? 0.5 : 1 }}
                                            onClick={generatingFlashingPdf ? undefined : handleOpenFlashingPreview}
                                            title={generatingFlashingPdf ? 'Regenerating…' : 'Preview Flashing PDF'}
                                        >
                                            <GrDocumentPdf className='DocIcon' />
                                        </SpanTag>
                                        <div
                                            className="docFilename"
                                            style={{ fontSize: '11px', fontWeight: 600, textAlign: 'center', wordBreak: 'break-all', margin: '4px 0' }}
                                            title={flashingPdf.fileName}
                                        >
                                            {flashingPdf.fileName || 'Flashing PDF'}
                                        </div>
                                        <Tooltip title="Delete Flashing PDF">
                                            <IconButton aria-label="delete-flashing-pdf" color="secondary" onClick={handleDeleteFlashingPdf}>
                                                <MdDelete />
                                            </IconButton>
                                        </Tooltip>
                                        <a
                                            href="#preview-flashing-pdf"
                                            onClick={(e) => { e.preventDefault(); if (!generatingFlashingPdf) handleOpenFlashingPreview(); }}
                                            title={generatingFlashingPdf ? 'Regenerating…' : 'Preview Flashing PDF'}
                                            style={{ pointerEvents: generatingFlashingPdf ? 'none' : 'auto', opacity: generatingFlashingPdf ? 0.6 : 1 }}
                                        >
                                            {generatingFlashingPdf ? 'Regenerating…' : 'Preview'}
                                        </a>
                                    </MyDiv>
                                </Col>
                            )}
                        </Row>
                    </MyDiv>
                    {/* Production Info entry — System Generated messages are automatic;
                        prefill picks + manual text are clubbed with them into one
                        block on the Flashing / GBI PDF. Shown only when this module
                        allows the system-generated PDF (the text feeds that PDF). */}
                    {(department === 'F' || department === 'GBI') && allowSystemGenerated && (
                        <MyDiv className="productionInfoEntry mt-2" style={{ borderTop: '1px solid #eee', paddingTop: '8px' }}>
                            <div style={{ fontWeight: 700, marginBottom: '2px' }}>Production Info (prints on the PDF)</div>
                            <div style={{ fontSize: '12px', color: '#777', marginBottom: '6px' }}>System-generated messages are added automatically.</div>

                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', marginBottom: '8px' }}>
                                {PREFILL_OPTIONS.map((opt) => (
                                    <label key={opt.key} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '13px', cursor: 'pointer', margin: 0 }}>
                                        <input type="checkbox" checked={prefillKeys.includes(opt.key)} onChange={() => togglePrefill(opt.key)} />
                                        {opt.label}
                                    </label>
                                ))}
                            </div>

                            <textarea
                                value={prodInfoText}
                                onChange={(e) => handleProdInfoTextChange(e.target.value)}
                                placeholder="Manually entered production information..."
                                rows={2}
                                maxLength={500}
                                style={{ width: '100%', fontSize: '13px', padding: '6px' }}
                            />
                            <div style={{ fontSize: '11px', color: '#999', textAlign: 'right' }}>{prodInfoText.length}/500</div>
                        </MyDiv>
                    )}
                </DialogContent>
                <DialogActions className='d-flex justify-content-start flex-column'>
                    <Row className='dialogFooter'>
                        {/* Manual Upload — the Production Info module's policy only
                            governs Flashing (F) and GBI. Every other department keeps
                            its upload form unconditionally, as before the module existed. */}
                        <Col md={6} className=''>
                            {((department !== 'F' && department !== 'GBI') || allowManualUpload) && (
                                <form onSubmit={handleSubmit}>
                                    <MyDiv className="mb-2">
                                        <input type="file" id="document" name="document" multiple onChange={handleFileSelect} />
                                        <Button className='btn primary-btn mb-1' type="submit" disabled={btnLoading || !selectedFiles.length}>
                                            {btnLoading ? <Spinner animation="border" size="sm" /> : "Upload"}
                                        </Button>
                                    </MyDiv>
                                </form>
                            )}
                            {CoreOrder && CoreOrder.order_myob_push_status === true && (
                                <MyDiv><Badge pill className='orderMovedToProdBtn mb-3'>Order Data's sent to MYOB</Badge></MyDiv>
                            )}
                        </Col>
                        {/* ) : null} */}
                        {/* Flashing PDF is per-unit aware (scopes by flashingUnitId),
                            so its button shows even when a sub-unit is selected.
                            GBI is order-level only, so it stays hidden once a unit
                            is active. Each tracks its own loading + exists state. */}
                        {allowSystemGenerated && (department === 'GBI' || department === 'F') && (department === 'F' || !unitDbId) && (() => {
                            const isFlashing = department === 'F';
                            const loading = isFlashing ? generatingFlashingPdf : pdfloading;
                            const alreadyGenerated = isFlashing ? !!flashingPdf?.exists : hasGeneratedPdf;
                            return (
                                <Col md={6} className='d-flex justify-content-end mb-2'>
                                    <Button
                                        onClick={() => isFlashing ? handleGenerateFlashingPdf() : generateGbiSystemPdf()}
                                        className="btn primary-btn"
                                        style={{ whiteSpace: 'nowrap', marginRight: '10px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                                        disabled={loading}
                                    >
                                        <MdPictureAsPdf />
                                        {loading
                                            ? <span>Generating PDF...</span>
                                            : <span>{alreadyGenerated ? 'Regenerate PDF' : 'Generate PDF'}</span>}
                                    </Button>
                                </Col>
                            );
                        })()}
                    </Row>
                </DialogActions>
            </Dialog>

            {/* PDF preview popup — iframe-based inline viewer triggered by
                clicking a PDF icon in the documents grid or after the
                system-generated PDF is attached. Uses the same signed S3
                URL the rest of the dialog uses. */}
            <Dialog
                open={Boolean(previewUrl)}
                onClose={() => setPreviewUrl(null)}
                fullWidth
                maxWidth="lg"
                className="GeneralModal"
            >
                <DialogTitle className='space-between'>
                    PDF Preview
                    <Button onClick={() => setPreviewUrl(null)} className="btn"> X </Button>
                </DialogTitle>
                <DialogContent style={{ padding: 0, height: '80vh' }}>
                    {previewUrl && (
                        <iframe
                            src={previewUrl}
                            title="PDF Preview"
                            style={{ width: '100%', height: '100%', border: 'none' }}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </React.Fragment>
    );
}

OrderDocuments.propTypes = {
    selectedImages: PropTypes.array,
    orderUpdates: PropTypes.func.isRequired,
    CoreOrder: PropTypes.object,
    department: PropTypes.string.isRequired,
    unitDbId: PropTypes.string,
    flashingUnitId: PropTypes.string,
    flashingUnitOrderNumber: PropTypes.string
};

export default OrderDocuments;
